import { Injectable, Signal, effect, inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import {CurrentUserAdminGQL, UserAdmin} from "../../../generated/graphql";
import {Observable} from "rxjs";
import {environment} from "../../../environments/environment";

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private tokenKey = 'token';
    private platformId = inject(PLATFORM_ID);

    public BASE_URL = environment.base_url;

    token = signal<string | null>(this.getTokenFromStorage());

    private readonly _userSignal = signal<UserAdmin | null>(null);
    readonly userSignal = this._userSignal.asReadonly();

    private isFetchingUser = false;

    constructor(
        private currentUserGQL: CurrentUserAdminGQL,
        private router: Router
    ) {
        effect(() => {
            if (this.isAuthenticated() && !this.userSignal() && !this.isFetchingUser) {
                this.fetchCurrentUser();
            }
        });
    }

    login(token: string) {
        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(this.tokenKey, token);
        }
        this.token.set(token);
        this.fetchCurrentUser();
    }

    logout() {
        this.token.set(null);
        this._userSignal.set(null);
        if (isPlatformBrowser(this.platformId)) {
            localStorage.removeItem(this.tokenKey);
            this.router.navigate(['/login']);
        }
    }

    isAuthenticated(): boolean {
        return !!this.token();
    }

    fetchCurrentUser() {
        if (!this.isAuthenticated() || this.isFetchingUser) return;

        this.isFetchingUser = true;

        this.currentUserGQL.watch().valueChanges.subscribe(
            ({ data }) => {
                if (data?.currentUserAdmin) {
                    this.updateUser(data.currentUserAdmin as UserAdmin);
                }
                this.isFetchingUser = false;
            },
            error => {
                console.log('Erro ao buscar usuário:', error);
                this.logout();
                this.isFetchingUser = false;
            }
        );
    }

    updateUser(user: UserAdmin) {
        this._userSignal.set(user);
    }

    getUserSignal(): Signal<UserAdmin | null> {
        return this.userSignal;
    }

    private getTokenFromStorage(): string | null {
        return isPlatformBrowser(this.platformId) ? localStorage.getItem(this.tokenKey) : null;
    }

    upload(file: any, pathUrl: string, type?: string) {
        let url = `${this.BASE_URL}/upload/${pathUrl}`;
        if (type != null) {
            url = url + '/' + type;
        }

        return new Observable((observer) => {
            const formData: FormData = new FormData();
            const xhr: XMLHttpRequest = new XMLHttpRequest();

            if (file instanceof File) {
                formData.append('file', file, file.name);
            } else {
                observer.error('Arquivo não é válido.');
                return;
            }

            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        observer.next(JSON.parse(xhr.response));
                        observer.complete();
                    } else {
                        observer.error(xhr.response);
                    }
                }
            };

            xhr.upload.onprogress = (event) => {
                const progress = Math.round((event.loaded / event.total) * 100);
                console.log(`Progresso: ${progress}%`);
            };

            xhr.open('POST', url, true);
            xhr.setRequestHeader('Authorization', 'Bearer ' + this.getTokenFromStorage());
            xhr.send(formData);
        });
    }
}
